import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Search, Mail, Phone, Loader2, AlertCircle,
  Edit2, Trash2, KeyRound, Eye, EyeOff, X, Plus,
  ShieldCheck, GraduationCap
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const roleColors = {
  admin: 'bg-[#D72B2B]/10 text-[#D72B2B]',
  teacher: 'bg-[#1A2E6E]/10 text-[#1A2E6E]'
};

const roleIcons = {
  admin: ShieldCheck,
  teacher: GraduationCap
};

const emptyForm = { name: '', email: '', phone: '', password: '', confirm: '', role: 'teacher' };

export const AdminTeachers = () => {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Create / Edit modal (shared)
  const [modal, setModal] = useState(null); // null | 'create' | 'edit'
  const [selectedUser, setSelectedUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  // Password reset modal
  const [passwordUser, setPasswordUser] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ new_password: '', confirm: '' });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Delete
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchTeachers();
  }, []);

  const fetchTeachers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/teachers?include_inactive=true&page_size=200`);
      setTeachers(response.data.data ?? response.data);
    } catch (err) {
      console.error('Failed to fetch teachers:', err);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setSelectedUser(null);
    setForm(emptyForm);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setModal('create');
  };

  const openEdit = (user) => {
    setSelectedUser(user);
    setForm({ name: user.name || '', email: user.email || '', phone: user.phone || '', password: '', confirm: '', role: user.role });
    setShowPassword(false);
    setModal('edit');
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email are required');
      return;
    }

    setSaving(true);
    try {
      if (modal === 'create') {
        if (form.password.length < 8) {
          toast.error('Password must be at least 8 characters');
          setSaving(false);
          return;
        }
        if (form.password !== form.confirm) {
          toast.error('Passwords do not match');
          setSaving(false);
          return;
        }
        await axios.post(`${API_URL}/api/admin/users`, {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          password: form.password,
          role: form.role,
          status: 'approved'
        });
        toast.success(`${form.role === 'admin' ? 'Admin' : 'Teacher'} account created`);
      } else {
        await axios.put(`${API_URL}/api/admin/users/${selectedUser.id}`, {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null
        });
        toast.success('Profile updated');
      }
      setModal(null);
      fetchTeachers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const openPasswordReset = (user) => {
    setPasswordUser(user);
    setPasswordForm({ new_password: '', confirm: '' });
    setShowNewPassword(false);
    setShowConfirmNewPassword(false);
  };

  const handlePasswordReset = async () => {
    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setSavingPassword(true);
    try {
      await axios.put(`${API_URL}/api/admin/users/${passwordUser.id}/password`, {
        new_password: passwordForm.new_password
      });
      toast.success(`Password reset for ${passwordUser.name}`);
      setPasswordUser(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleDelete = async (user) => {
    const confirmed = window.confirm(
      `Permanently delete ${user.name} (${user.role})? This cannot be undone.`
    );
    if (!confirmed) return;
    setDeletingId(user.id);
    try {
      await axios.delete(`${API_URL}/api/admin/users/${user.id}`);
      toast.success(`${user.name} deleted`);
      fetchTeachers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeletingId(null);
    }
  };

  const filteredTeachers = teachers.filter(t =>
    t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-teachers-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">Team</h1>
          <p className="text-[#1A2E6E]/60 mt-1">Manage admin and teacher accounts</p>
        </div>
        <button
          onClick={openCreate}
          data-testid="admin-add-member-btn"
          className="flex items-center gap-2 px-5 py-2.5 bg-[#D72B2B] text-white rounded-xl hover:bg-[#B71C1C] transition-colors font-medium"
        >
          <Plus size={18} />
          Add Member
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-[#D72B2B]/5 rounded-xl border border-[#D72B2B]/20 p-4 text-center">
          <p className="text-2xl font-bold text-[#D72B2B]">
            {teachers.filter(t => t.role === 'admin').length}
          </p>
          <p className="text-sm text-[#1A2E6E]/60">Admins</p>
        </div>
        <div className="bg-[#1A2E6E]/5 rounded-xl border border-[#1A2E6E]/20 p-4 text-center">
          <p className="text-2xl font-bold text-[#1A2E6E]">
            {teachers.filter(t => t.role === 'teacher').length}
          </p>
          <p className="text-sm text-[#1A2E6E]/60">Teachers</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A2E6E]/40" />
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none transition-all"
        />
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden">
        {filteredTeachers.length > 0 ? (
          <div className="divide-y divide-[#E8EAF0]">
            {filteredTeachers.map((member, index) => {
              const RoleIcon = roleIcons[member.role] || ShieldCheck;
              return (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-4 lg:p-6 flex flex-col lg:flex-row lg:items-center gap-4"
                >
                  {/* Avatar & Info */}
                  <div className="flex items-center gap-4 flex-1">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg shrink-0 ${member.role === 'admin' ? 'bg-[#D72B2B]/10 text-[#D72B2B]' : 'bg-[#1A2E6E]/10 text-[#1A2E6E]'}`}>
                      {member.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-[#1A2E6E] truncate">{member.name}</p>
                      <div className="flex items-center gap-4 text-sm text-[#1A2E6E]/60">
                        <span className="flex items-center gap-1 truncate">
                          <Mail size={14} />
                          {member.email}
                        </span>
                        {member.phone && (
                          <span className="hidden sm:flex items-center gap-1">
                            <Phone size={14} />
                            {member.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Role Badge */}
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium w-fit ${roleColors[member.role]}`}>
                    <RoleIcon size={16} />
                    {member.role?.charAt(0).toUpperCase() + member.role?.slice(1)}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEdit(member)}
                      title="Edit profile"
                      className="p-2 text-[#1A2E6E]/60 hover:text-[#1A2E6E] hover:bg-[#1A2E6E]/5 rounded-lg transition-colors"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => openPasswordReset(member)}
                      title="Reset password"
                      className="p-2 text-[#F5A623]/70 hover:text-[#F5A623] hover:bg-[#F5A623]/10 rounded-lg transition-colors"
                    >
                      <KeyRound size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(member)}
                      disabled={deletingId === member.id}
                      title="Delete"
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {deletingId === member.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-[#1A2E6E]/30 mx-auto mb-4" />
            <p className="text-[#1A2E6E]/60">
              {searchTerm ? 'No team members match your search' : 'No team members yet'}
            </p>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      <AnimatePresence>
        {modal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md"
            >
              <div className="flex items-center justify-between p-6 border-b border-[#E8EAF0]">
                <h2 className="text-lg font-bold text-[#1A2E6E]">
                  {modal === 'create' ? 'Add Team Member' : 'Edit Profile'}
                </h2>
                <button onClick={() => setModal(null)} className="p-2 hover:bg-[#1A2E6E]/5 rounded-lg">
                  <X size={20} className="text-[#1A2E6E]/60" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                {/* Role (only on create) */}
                {modal === 'create' && (
                  <div>
                    <label className="block text-sm font-medium text-[#1A2E6E] mb-2">Role</label>
                    <div className="flex gap-3">
                      {['teacher', 'admin'].map(r => (
                        <button
                          key={r}
                          type="button"
                          onClick={() => setForm(f => ({ ...f, role: r }))}
                          data-testid={`team-role-${r}`}
                          className={`flex-1 py-2.5 rounded-xl border-2 text-sm font-medium transition-all ${
                            form.role === r
                              ? r === 'admin'
                                ? 'border-[#D72B2B] bg-[#D72B2B]/5 text-[#D72B2B]'
                                : 'border-[#1A2E6E] bg-[#1A2E6E]/5 text-[#1A2E6E]'
                              : 'border-[#E8EAF0] text-[#1A2E6E]/60'
                          }`}
                        >
                          {r.charAt(0).toUpperCase() + r.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Full Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    data-testid="team-name-input"
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    data-testid="team-email-input"
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Phone <span className="text-[#1A2E6E]/40">(optional)</span></label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    data-testid="team-phone-input"
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                  />
                </div>

                {modal === 'create' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Password</label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={form.password}
                          onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                          placeholder="At least 8 characters"
                          data-testid="team-password-input"
                          className="w-full px-4 py-3 pr-12 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(v => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1A2E6E]/40 hover:text-[#1A2E6E]"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Confirm Password</label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={form.confirm}
                          onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
                          placeholder="Repeat the password"
                          data-testid="team-confirm-password-input"
                          className="w-full px-4 py-3 pr-12 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(v => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1A2E6E]/40 hover:text-[#1A2E6E]"
                        >
                          {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="flex gap-3 p-6 pt-0">
                <button
                  onClick={() => setModal(null)}
                  className="flex-1 py-3 rounded-xl border border-[#E8EAF0] text-[#1A2E6E] hover:bg-[#1A2E6E]/5 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="team-save-btn"
                  className="flex-1 py-3 rounded-xl bg-[#D72B2B] text-white hover:bg-[#B71C1C] transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                  {modal === 'create' ? 'Create Account' : 'Save Changes'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Reset Password Modal */}
      <AnimatePresence>
        {passwordUser && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md"
            >
              <div className="flex items-center justify-between p-6 border-b border-[#E8EAF0]">
                <div>
                  <h2 className="text-lg font-bold text-[#1A2E6E]">Reset Password</h2>
                  <p className="text-sm text-[#1A2E6E]/60 mt-0.5">{passwordUser.name}</p>
                </div>
                <button onClick={() => setPasswordUser(null)} className="p-2 hover:bg-[#1A2E6E]/5 rounded-lg">
                  <X size={20} className="text-[#1A2E6E]/60" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-1">New Password</label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordForm.new_password}
                      onChange={e => setPasswordForm(f => ({ ...f, new_password: e.target.value }))}
                      placeholder="At least 8 characters"
                      className="w-full px-4 py-3 pr-12 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1A2E6E]/40 hover:text-[#1A2E6E]"
                    >
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirmNewPassword ? 'text' : 'password'}
                      value={passwordForm.confirm}
                      onChange={e => setPasswordForm(f => ({ ...f, confirm: e.target.value }))}
                      placeholder="Repeat the password"
                      className="w-full px-4 py-3 pr-12 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmNewPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1A2E6E]/40 hover:text-[#1A2E6E]"
                    >
                      {showConfirmNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 p-6 pt-0">
                <button
                  onClick={() => setPasswordUser(null)}
                  className="flex-1 py-3 rounded-xl border border-[#E8EAF0] text-[#1A2E6E] hover:bg-[#1A2E6E]/5 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePasswordReset}
                  disabled={savingPassword}
                  className="flex-1 py-3 rounded-xl bg-[#F5A623] text-white hover:bg-[#E09000] transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {savingPassword ? <Loader2 size={16} className="animate-spin" /> : null}
                  Reset Password
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminTeachers;
